import { SingleSelect_default } from './chunk-6UBOFWLJ.js';
import { ErrorBoundary_default } from './chunk-NJFJDBQA.js';
import { ThemeProvider } from './chunk-J2FXRRBJ.js';
import { __spreadValues, __spreadProps, __objRest } from './chunk-2FEJBM4N.js';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import * as React14 from 'react';
import React14__default, { useState, useMemo, useCallback } from 'react';
import { useTheme, useMediaQuery, Box, Table, TableBody, Stack, Typography, FormControl, Select, MenuItem, IconButton, CircularProgress, TableHead, TableRow, Checkbox, Tooltip, Icon, TableCell, Skeleton, ButtonBase } from '@mui/material';
import { get, set, cloneDeep } from 'lodash';
import FilterListIcon from '@mui/icons-material/FilterList';
import moment from 'moment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import throttle from 'lodash/throttle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';
import { CsvBuilder } from 'filefy';
import RefreshIcon from '@mui/icons-material/Refresh';

var TableButton = ({
  children,
  onClick,
  disabled = false
}) => {
  return /* @__PURE__ */ React14__default.createElement(
    IconButton,
    {
      disabled,
      sx: {
        bgcolor: "#F6f6f6",
        "&:hover": {
          bgcolor: "#EEEEEE"
        },
        borderRadius: "4px",
        height: "40px",
        px: "16px",
        fontFamily: "Sora",
        fontWeight: 600,
        color: "#000",
        fontSize: "12px"
      },
      onClick: (e) => {
        onClick(e.currentTarget);
      }
    },
    children
  );
};
var TableButton_default = TableButton;

// src/components/CustomMaterialTable/components/CollapsableComp.tsx
var CollapsableComp = ({
  allRowsCollapse,
  onChange
}) => {
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(TableButton_default, { onClick: onChange }, allRowsCollapse ? /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, "Collapse all", /* @__PURE__ */ React14__default.createElement(
    UnfoldLessIcon,
    {
      sx: { fontSize: "20px", fontWeight: 600, ml: "13px" }
    }
  )) : /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, "Expand all", /* @__PURE__ */ React14__default.createElement(
    UnfoldMoreIcon,
    {
      sx: { fontSize: "20px", fontWeight: 600, ml: "13px" }
    }
  ))));
};
var CollapsableComp_default = CollapsableComp;
var CustomMaterialTableContext = React14__default.createContext(
  {}
);
var useCustomMaterialTableContext = () => React14__default.useContext(CustomMaterialTableContext);
function CustomMaterialTableContextProvider(props) {
  const { children, stickyHeader } = props;
  const [customMaterialTableCompScrollEl, setCustomMaterialTableCompScrollEl] = React14__default.useState(null);
  const [customMaterialTableRootComp, setCustomMaterialTableRootComp] = React14__default.useState(null);
  const parentScrollableElement = React14__default.useMemo(() => {
    if (!customMaterialTableCompScrollEl) return null;
    let parent = customMaterialTableCompScrollEl.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (overflowY === "scroll" || overflowY === "auto") {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.documentElement;
  }, [customMaterialTableCompScrollEl]);
  const contextValue = React14__default.useMemo(() => {
    return {
      totalStickyTop: stickyHeader,
      customMaterialTableCompScrollEl,
      customMaterialTableRootComp,
      parentScrollableElement,
      setCustomMaterialTableCompScrollEl,
      setCustomMaterialTableRootComp
    };
  }, [
    customMaterialTableCompScrollEl,
    stickyHeader,
    customMaterialTableRootComp,
    parentScrollableElement,
    setCustomMaterialTableRootComp
  ]);
  return /* @__PURE__ */ React14__default.createElement(CustomMaterialTableContext.Provider, { value: contextValue }, children);
}
function formatCurrency(amount, obj) {
  const formatting_options = {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    notation: void 0 
  };
  const dollarString = new Intl.NumberFormat("en-US", formatting_options);
  return dollarString.format(amount);
}
function bodyTextAlign(type) {
  switch (type) {
    case "currency":
      return "right";
    case "center":
      return "center";
    case "date":
      return "left";
    default:
      return "left";
  }
}
function formatData(x, col) {
  if (col.alignType === "currency" && typeof x === "number") {
    return formatCurrency(x);
  }
  if (col.alignType === "date") {
    try {
      return moment(x).format("DD MMM YYYY");
    } catch (err) {
      return x;
    }
  }
  return x;
}
function generateSortableOptions(columns, value) {
  const result = columns.filter((col) => col.sortable !== false).map((col) => ({ label: col.title, value: col.field }));
  if (typeof value === "string") {
    return result.find((col) => col.value === value) || null;
  } else if (value) {
    return result.find((col) => col.value === value.value) || null;
  }
  return result;
}
function getExtraRowProps(params) {
  const { colData, colNumber, totalColumns, rowNumber, totalRows } = params;
  let obj = {
    title: colData.title,
    field: colData.field,
    rowNumber,
    // rowNumber is optional. It is rowIndex + 1
    totalRows,
    // totalRows is optional. It is total number of rows
    colNumber,
    // colNumber is optional. It is columnIndex + 1
    totalCols: totalColumns
  };
  return obj;
}
function CustomMaterialTableCell(props) {
  const _a = props, { fixed } = _a, tableCellProps = __objRest(_a, ["fixed"]);
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const { customMaterialTableCompScrollEl, customMaterialTableRootComp } = useCustomMaterialTableContext();
  const [maxWidth, setMaxWidth] = React14__default.useState(null);
  const tableCellRef = React14__default.useRef(null);
  const [isFixed, setIsFixed] = React14__default.useState(null);
  const formatCssNumbers = (value) => {
    if (value.includes("px")) {
      return Number(value.replace("px", "")) || 0;
    }
    return Number(value);
  };
  React14__default.useEffect(() => {
    if (!fixed) return;
    if (!customMaterialTableCompScrollEl) return;
    const customMaterialTableComputedStyle = window.getComputedStyle(
      customMaterialTableCompScrollEl
    );
    const borderLeftWidth = formatCssNumbers(
      customMaterialTableComputedStyle.borderLeftWidth
    );
    const borderRightWidth = formatCssNumbers(
      customMaterialTableComputedStyle.borderRightWidth
    );
    const scrollbarWidth = customMaterialTableCompScrollEl.offsetWidth - customMaterialTableCompScrollEl.clientWidth;
    const onScroll = () => {
      const tableCellEl = tableCellRef.current;
      if (!tableCellEl) return;
      const tableCellElRect = tableCellEl.getBoundingClientRect();
      const customMaterialTableCompRootElRect = customMaterialTableCompScrollEl.getBoundingClientRect();
      const leftDifference = Math.abs(
        tableCellElRect.left - customMaterialTableCompRootElRect.left
      );
      const rightDifference = Math.abs(
        tableCellElRect.right - customMaterialTableCompRootElRect.right
      );
      if (fixed === "left" && leftDifference < borderLeftWidth + 0.5 && customMaterialTableCompScrollEl.scrollLeft > 0) {
        setIsFixed("left");
      } else if (fixed === "right" && rightDifference < borderRightWidth + scrollbarWidth + 0.5) {
        setIsFixed("right");
      } else {
        setIsFixed(null);
      }
    };
    onScroll();
    customMaterialTableCompScrollEl.addEventListener("scroll", onScroll);
    return () => {
      customMaterialTableCompScrollEl.removeEventListener("scroll", onScroll);
    };
  }, [fixed, customMaterialTableCompScrollEl]);
  React14__default.useEffect(() => {
    if (!customMaterialTableRootComp || !sm) return;
    const { width } = customMaterialTableRootComp.getBoundingClientRect();
    setMaxWidth(width / 3);
  }, [customMaterialTableRootComp, sm]);
  return /* @__PURE__ */ React14__default.createElement(
    TableCell,
    __spreadProps(__spreadValues({}, tableCellProps), {
      ref: tableCellRef,
      sx: __spreadValues(__spreadValues(__spreadValues({}, sm ? {
        maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : void 0
      } : {}), tableCellProps.sx), fixed ? __spreadProps(__spreadValues({
        position: "sticky",
        left: fixed === "left" ? 0 : void 0,
        right: fixed === "right" ? 0 : void 0,
        zIndex: fixed === "left" ? 3 : 2
      }, isFixed ? {
        borderRight: isFixed === "left" ? "1px solid rgba(224, 224, 224, 1)" : void 0,
        borderLeft: isFixed === "right" ? "1px solid rgba(224, 224, 224, 1)" : void 0
      } : {}), {
        "&:before": {
          content: '""',
          pointerEvents: "none",
          position: "absolute",
          top: "-1px",
          bottom: "-1px",
          width: "4px",
          transition: "box-shadow 0.2s linear",
          left: fixed === "right" ? 0 : void 0,
          right: fixed === "left" ? 0 : void 0,
          boxShadow: isFixed === "left" ? "4px 0 4px 0px rgba(0, 0, 0, 0.15)" : isFixed === "right" ? "-4px 0 4px 0px rgba(0, 0, 0, 0.15)" : "0px 0px 0px 0px rgba(0, 0, 0, 0)"
        }
      }) : {})
    })
  );
}

// src/components/CustomMaterialTable/helpers/CustomMaterialTableHeader.tsx
function CustomMaterialTableHeader(props) {
  const {
    headerRowStyle,
    detailPanel,
    detailRows,
    showDetailPanelOnRowClick,
    showDetailPanelArrow,
    defaultHeaderStyle,
    headerCellStyle,
    subHeaderCellStyle,
    subHeaderRowStyle,
    rowSelectable,
    columns,
    sortable,
    sortBy,
    orderBy,
    actions,
    components,
    onSortByChange
  } = props;
  const { detailPanelComp, rowSelectableComp, actionsComp } = React14__default.useMemo(() => {
    return {
      detailPanelComp: (detailPanel || detailRows) && !showDetailPanelOnRowClick && showDetailPanelArrow !== false && /* @__PURE__ */ React14__default.createElement(
        CustomMaterialTableCell,
        {
          className: "detail-panel-cell",
          sx: __spreadValues(__spreadValues({}, defaultHeaderStyle || {}), headerCellStyle || {})
        }
      ),
      rowSelectableComp: rowSelectable && /* @__PURE__ */ React14__default.createElement(
        CustomMaterialTableCell,
        {
          className: "row-select-cell",
          sx: __spreadValues(__spreadValues({}, defaultHeaderStyle || {}), headerCellStyle || {})
        }
      ),
      actionsComp: (actions || (components == null ? void 0 : components.Actions)) && /* @__PURE__ */ React14__default.createElement(
        CustomMaterialTableCell,
        {
          className: "action-cell",
          sx: __spreadValues(__spreadValues({}, defaultHeaderStyle), headerCellStyle),
          fixed: "right"
        }
      )
    };
  }, [
    detailPanel,
    detailRows,
    showDetailPanelOnRowClick,
    showDetailPanelArrow,
    rowSelectable,
    actions,
    components == null ? void 0 : components.Actions,
    defaultHeaderStyle,
    headerCellStyle
  ]);
  const hasSubHeaders = React14__default.useMemo(() => {
    return columns == null ? void 0 : columns.some((col) => col.subHeaderRender);
  }, [columns]);
  const subHeaderRenders = React14__default.useMemo(() => {
    if (!hasSubHeaders) return [];
    return columns == null ? void 0 : columns.map((col, i) => {
      const extraRowProps = getExtraRowProps({
        colData: col,
        colNumber: i + 1,
        totalColumns: columns.length
      });
      return {
        colData: col,
        extraProps: extraRowProps,
        render: (col == null ? void 0 : col.subHeaderRender) || (() => /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null))
      };
    });
  }, [columns, hasSubHeaders]);
  const subHeaderRowStyleResult = React14__default.useMemo(() => {
    return __spreadValues(__spreadValues({}, headerRowStyle || {}), subHeaderRowStyle || {});
  }, [subHeaderRowStyle, headerRowStyle]);
  const isFixed = React14__default.useCallback((index, fixed) => {
    return index === 0 && !fixed ? "left" : fixed;
  }, []);
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(TableHead, null, /* @__PURE__ */ React14__default.createElement(
    TableRow,
    {
      sx: __spreadValues({
        bgcolor: "#FBFAFA",
        position: "sticky",
        top: "-1px",
        zIndex: 4
      }, headerRowStyle || {})
    },
    detailPanelComp,
    rowSelectableComp,
    columns == null ? void 0 : columns.map((col, i) => {
      const isSortable = sortable && col.sortable !== false;
      const isActiveSort = isSortable && sortBy === col.field;
      const fixed = isFixed(i, col.fixed);
      return /* @__PURE__ */ React14__default.createElement(
        CustomMaterialTableCell,
        {
          key: i,
          fixed,
          sx: __spreadValues(__spreadValues(__spreadProps(__spreadValues({}, defaultHeaderStyle), {
            textAlign: bodyTextAlign(col.alignType),
            cursor: isSortable ? "pointer" : "auto",
            "& .filter-icon": {
              opacity: isSortable ? isActiveSort ? 1 : 0 : 0
            },
            "&:hover .filter-icon": {
              opacity: isSortable ? isActiveSort ? 1 : 0.8 : 0
            }
          }), headerCellStyle), col.headerStyle),
          onClick: () => {
            if (isSortable && onSortByChange) {
              onSortByChange({ label: col.title, value: col.field });
            }
          }
        },
        /* @__PURE__ */ React14__default.createElement(
          Stack,
          {
            direction: "row",
            alignItems: "center",
            gap: "5px",
            sx: { position: "relative" }
          },
          /* @__PURE__ */ React14__default.createElement(Box, { sx: { flex: 1, maxWidth: "100%" } }, col.header, !col.header && /* @__PURE__ */ React14__default.createElement(
            Typography,
            {
              variant: "subtitle2",
              sx: __spreadValues({
                fontWeight: 600,
                color: "#000",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }, col.headerTextStyle || {})
            },
            col.title
          )),
          isSortable && /* @__PURE__ */ React14__default.createElement(
            "div",
            {
              className: "filter-icon",
              style: {
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                transition: "0.2s",
                transformOrigin: "center",
                transform: `rotateZ(${isActiveSort ? orderBy === "ascending" ? 180 : 0 : 0}deg)`,
                position: "absolute",
                right: "-18px",
                top: 0,
                bottom: 0
              }
            },
            /* @__PURE__ */ React14__default.createElement(FilterListIcon, { color: "inherit", fontSize: "inherit" })
          )
        )
      );
    }),
    actionsComp
  ), hasSubHeaders && /* @__PURE__ */ React14__default.createElement(
    TableRow,
    {
      sx: __spreadValues({
        bgcolor: "#FBFAFA",
        position: "sticky",
        top: "-1px",
        zIndex: 3
      }, subHeaderRowStyleResult)
    },
    detailPanelComp,
    rowSelectableComp,
    subHeaderRenders.map((item, i) => {
      const { colData: col, extraProps, render: subHeaderRender } = item;
      const fixed = isFixed(i, col.fixed);
      return /* @__PURE__ */ React14__default.createElement(
        CustomMaterialTableCell,
        {
          key: i,
          fixed,
          sx: __spreadValues(__spreadValues(__spreadProps(__spreadValues({}, defaultHeaderStyle), {
            textAlign: bodyTextAlign(col.alignType)
          }), headerCellStyle), col.headerStyle)
        },
        subHeaderRender(extraProps)
      );
    }),
    actionsComp
  )));
}
var LoadingSkeleton = () => /* @__PURE__ */ React14.createElement(Skeleton, { variant: "rounded", width: "100%", height: "12px" });
function CustomMaterialTableRow(props) {
  var _a, _b;
  const { data: item, columns, options, rowIndex, totalRows, loading } = props;
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDetailPanel, setOpenDetailPanel] = React14.useState(false);
  React14.useEffect(() => {
    if (props.expandedDetailPanels) {
      setOpenDetailPanel(props.expandedDetailPanels(item, rowIndex));
    } else {
      setOpenDetailPanel(props.allRowsCollapse);
    }
  }, [props.allRowsCollapse, props.expandedDetailPanels, item, rowIndex]);
  const isChecked = React14.useMemo(() => {
    if (typeof props.selectedRowIdentifier === "function") {
      return props.selectedArr.some((selectedItem) => {
        return props.selectedRowIdentifier(selectedItem) === props.selectedRowIdentifier(item);
      });
    }
    return props.selectedArr.includes(item);
  }, [item, props.selectedArr, props.selectedRowIdentifier]);
  const hasDetailSection = React14.useMemo(() => {
    return props.detailPanel || props.detailRows;
  }, [props.detailPanel, props.detailRows]);
  const detailPanelColSpan = React14.useMemo(() => {
    let colSpan = columns.length;
    if (options == null ? void 0 : options.rowSelectable) {
      colSpan += 1;
    }
    if (props.detailPanel) {
      colSpan += 1;
    }
    return colSpan;
  }, [columns, options == null ? void 0 : options.rowSelectable, props.detailPanel]);
  const defaultBodyStyle = React14.useMemo(() => {
    let style = {
      // padding: '25px 30px',
      px: sm ? 2 : 3,
      py: sm ? 1 : 3
    };
    if (hasDetailSection) {
      style = __spreadProps(__spreadValues({}, style), {
        borderBottom: "1px solid #F1F1F1"
      });
    } else {
      style = __spreadProps(__spreadValues({}, style), {
        borderTop: "1px solid #F1F1F1",
        borderBottom: "1px solid #F1F1F1"
      });
    }
    return style;
  }, [hasDetailSection, sm]);
  const getExtraRowProp = React14.useCallback(
    (colData, colIndex) => {
      return getExtraRowProps({
        colData,
        colNumber: colIndex + 1,
        totalColumns: columns.length,
        rowNumber: rowIndex + 1,
        totalRows
      });
    },
    [props.data, columns]
  );
  const openDetailPanelColor = "#EBECF2";
  const rowBackgroundColor = React14.useMemo(() => {
    if (openDetailPanel && !(options == null ? void 0 : options.disableDetailPanelRowHighlight)) {
      return openDetailPanelColor;
    }
    if (rowIndex % 2 === 0) {
      return "#FFF";
    }
    return "#FBFAFA";
  }, [rowIndex, openDetailPanel, options == null ? void 0 : options.disableDetailPanelRowHighlight]);
  return /* @__PURE__ */ React14.createElement(React14.Fragment, null, /* @__PURE__ */ React14.createElement(
    TableRow,
    {
      sx: [
        {
          background: rowBackgroundColor,
          "&>*": {
            cursor: props.onRowClick ? "pointer !important" : "auto"
          },
          "&:hover": {
            background: props.onRowClick ? "rgba(240, 240, 240, 1)" : rowBackgroundColor
          }
        },
        get(options, "bodyRowStyle", {})
      ],
      onClick: () => {
        var _a2;
        if (loading) return;
        (_a2 = props.onRowClick) == null ? void 0 : _a2.call({}, item);
        if (options.showDetailPanelOnRowClick) {
          setOpenDetailPanel(!openDetailPanel);
        }
      }
    },
    hasDetailSection && !options.showDetailPanelOnRowClick && options.showDetailPanelArrow !== false && /* @__PURE__ */ React14.createElement(
      CustomMaterialTableCell,
      {
        sx: __spreadValues(__spreadProps(__spreadValues({}, defaultBodyStyle), {
          px: 0
        }), get(options, "bodyCellStyle", {}))
      },
      !props.hideDetailPanelIcon && /* @__PURE__ */ React14.createElement(
        IconButton,
        {
          sx: {
            color: "black",
            transition: "0.2s",
            transformOrigin: "center",
            transform: `rotateZ(${openDetailPanel ? 90 : 0}deg)`
          },
          onClick: (e) => {
            e.stopPropagation();
            setOpenDetailPanel((prev) => !prev);
          }
        },
        /* @__PURE__ */ React14.createElement(ChevronRightIcon, { fontSize: "inherit", color: "inherit" })
      )
    ),
    (options == null ? void 0 : options.rowSelectable) && /* @__PURE__ */ React14.createElement(
      CustomMaterialTableCell,
      __spreadValues({
        sx: [
          __spreadValues({}, defaultBodyStyle),
          get(options, "bodyCellStyle", {})
        ]
      }, options && options.selectableTableCellProps ? typeof options.selectableTableCellProps === "function" ? options.selectableTableCellProps(item) : options.selectableTableCellProps : {}),
      /* @__PURE__ */ React14.createElement(
        Checkbox,
        {
          checked: isChecked,
          onClick: (e) => {
            if (props.onChangeSelectedData) {
              e.stopPropagation();
              props.onChangeSelectedData(item);
            }
          }
        }
      )
    ),
    columns.map((col, j) => {
      if (typeof col.removeCell === "function" && col.removeCell(item, getExtraRowProp(col, j)) || typeof col.removeCell === "boolean" && col.removeCell) {
        return /* @__PURE__ */ React14.createElement(React14.Fragment, { key: rowIndex + "-" + j });
      }
      const fixed = j === 0 && !col.fixed ? "left" : col.fixed;
      return /* @__PURE__ */ React14.createElement(
        CustomMaterialTableCell,
        {
          key: rowIndex + "-" + j,
          fixed: props.subRow ? null : fixed,
          colSpan: typeof col.colSpan === "function" ? col.colSpan(item, getExtraRowProp(col, j)) : col.colSpan,
          rowSpan: typeof col.rowSpan === "function" ? col.rowSpan(item, getExtraRowProp(col, j)) : col.rowSpan,
          sx: __spreadValues(__spreadValues(__spreadProps(__spreadValues({}, defaultBodyStyle), {
            textAlign: bodyTextAlign(col.alignType),
            cursor: col.onClick ? "pointer" : "auto",
            pl: props.subRow && j === 0 ? 6 : void 0,
            bgcolor: "inherit"
          }), get(options, "bodyCellStyle", {})), "cellStyle" in col ? typeof col["cellStyle"] === "function" ? col.cellStyle(item, getExtraRowProp(col, j)) : col.cellStyle : {}),
          onClick: () => {
            var _a2;
            if (loading) {
              return;
            }
            (_a2 = col.onClick) == null ? void 0 : _a2.call({}, item);
          }
        },
        loading ? /* @__PURE__ */ React14.createElement(LoadingSkeleton, null) : col.render ? col.render(item, getExtraRowProp(col, j)) : /* @__PURE__ */ React14.createElement(
          Typography,
          {
            variant: "subtitle2",
            sx: __spreadValues({
              fontWeight: j === 0 && 600,
              color: "#000"
            }, "textStyle" in col ? typeof col["textStyle"] === "function" ? col.textStyle(item, getExtraRowProp(col, j)) : col.textStyle : {})
          },
          formatData(get(item, `${col.field}`, ""), col)
        )
      );
    }),
    (props.actions || get(props, "components.Actions", false)) && /* @__PURE__ */ React14.createElement(
      CustomMaterialTableCell,
      {
        fixed: props.subRow ? null : "right",
        sx: { bgcolor: "inherit" }
      },
      get(props, "components.Actions", false) ? (_b = (_a = props.components) == null ? void 0 : _a.Actions) == null ? void 0 : _b.call({}, item, props.actions) : props.actions ? /* @__PURE__ */ React14.createElement(Stack, { direction: "row", alignItems: "center", gap: "5px" }, props.actions.map((action) => {
        return /* @__PURE__ */ React14.createElement(Tooltip, { key: action.icon, title: action.tooltip }, /* @__PURE__ */ React14.createElement(
          IconButton,
          {
            size: "small",
            onClick: () => {
              action.onClick(item);
            }
          },
          /* @__PURE__ */ React14.createElement(Icon, null, action.icon)
        ));
      })) : void 0
    )
  ), props.detailRows && openDetailPanel && (typeof props.detailRows === "function" ? props.detailRows(item) : props.detailRows).map((detailRow, j, detailArr) => {
    return /* @__PURE__ */ React14.createElement(React14.Fragment, { key: `${rowIndex}-${j}` }, /* @__PURE__ */ React14.createElement(
      CustomMaterialTableRow,
      {
        subRow: true,
        hideDetailPanelIcon: true,
        detailPanel: true,
        data: detailRow,
        columns,
        options,
        rowIndex: j,
        totalRows: detailArr.length,
        onRowClick: props.onRowClick,
        expandedDetailPanels: props.expandedDetailPanels,
        selectedArr: props.selectedArr,
        selectedRowIdentifier: props.selectedRowIdentifier,
        onChangeSelectedData: props.onChangeSelectedData,
        actions: props.actions,
        components: props.components
      }
    ));
  }), props.detailPanel && openDetailPanel && /* @__PURE__ */ React14.createElement(TableRow, null, /* @__PURE__ */ React14.createElement(CustomMaterialTableCell, { colSpan: detailPanelColSpan, sx: { p: 0 } }, /* @__PURE__ */ React14.createElement(
    Box,
    {
      sx: __spreadValues({
        px: "33px",
        pt: "8px",
        pb: "40px",
        bgcolor: openDetailPanelColor
      }, get(options, "detailPanelContainerStyle", {}))
    },
    /* @__PURE__ */ React14.createElement(
      Box,
      {
        sx: __spreadValues({}, get(options, "detailPanelStyle", {}))
      },
      typeof props.detailPanel === "function" ? props.detailPanel(item) : props.detailPanel
    )
  ))));
}

// src/components/CustomMaterialTable/components/CustomMaterialTableComp.tsx
var CustomMaterialTableComp = React14.forwardRef(function(props, ref) {
  const { options } = props;
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const { setCustomMaterialTableCompScrollEl, totalStickyTop } = useCustomMaterialTableContext();
  const [selectedRowsData, setSelectedRowsData] = React14.useState([]);
  const [rootHeight, setRootHeight] = React14.useState("auto");
  const [sortBy, setSortBy] = React14.useState(
    () => options.initialSortableOption ? typeof options.initialSortableOption === "string" ? options.initialSortableOption : options.initialSortableOption.value : ""
  );
  const [orderBy, setOrderBy] = React14.useState(
    options.initialOrderBy || "descending"
  );
  const rootRef = React14.useRef(null);
  const columns = React14.useMemo(() => {
    const result = props.columns.filter((col) => !col.hidden);
    if (props.isLoading && result.length === 0) {
      return [
        { title: "", field: "" },
        { title: "", field: "" },
        { title: "", field: "" }
      ];
    }
    return result;
  }, [props.columns, props.isLoading]);
  const data = React14.useMemo(() => {
    if (props.isLoading) {
      return [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
    }
    return props.data;
  }, [props.data, props.isLoading]);
  const defaultHeaderStyle = React14.useMemo(() => {
    let style = {
      px: sm ? 2 : 3,
      py: sm ? 1 : 2,
      borderBottom: "1px solid #F1F1F1",
      bgcolor: "#F9F9F9"
    };
    return style;
  }, [sm]);
  const onSortByChange = React14.useCallback(
    (selectedOption) => {
      let tempOrderBy = "descending";
      if (selectedOption.value === sortBy) {
        tempOrderBy = orderBy === "ascending" ? "descending" : "ascending";
      }
      setOrderBy(tempOrderBy);
      setSortBy(selectedOption.value);
      props.onSortByChange(selectedOption, tempOrderBy);
    },
    [sortBy, orderBy, props.onSortByChange]
  );
  const toggleSelectedData = React14.useCallback(
    (data2) => {
      let dataIndex = -1;
      if (typeof props.selectedRowIdentifier === "function") {
        dataIndex = selectedRowsData.findIndex(
          (item) => props.selectedRowIdentifier(item) === props.selectedRowIdentifier(data2)
        );
      } else {
        dataIndex = selectedRowsData.indexOf(data2);
      }
      let arr;
      if (dataIndex >= 0) {
        arr = selectedRowsData.slice(0, dataIndex).concat(selectedRowsData.slice(dataIndex + 1));
      } else {
        arr = [...selectedRowsData, data2];
      }
      setSelectedRowsData(arr);
      if (props.onSelectionChange) {
        props.onSelectionChange(JSON.parse(JSON.stringify(arr)));
      }
    },
    [props.selectedRowIdentifier, selectedRowsData, options == null ? void 0 : options.rowSelectable]
  );
  const tableHeaderProps = React14.useMemo(() => {
    return {
      columns,
      headerRowStyle: options == null ? void 0 : options.headerRowStyle,
      detailPanel: props.detailPanel,
      detailRows: props.detailRows,
      actions: props.actions,
      components: props.components,
      showDetailPanelOnRowClick: options == null ? void 0 : options.showDetailPanelOnRowClick,
      showDetailPanelArrow: options == null ? void 0 : options.showDetailPanelArrow,
      defaultHeaderStyle,
      headerCellStyle: options == null ? void 0 : options.headerCellStyle,
      subHeaderCellStyle: options == null ? void 0 : options.subHeaderCellStyle,
      subHeaderRowStyle: options == null ? void 0 : options.subHeaderRowStyle,
      rowSelectable: options == null ? void 0 : options.rowSelectable,
      sortable: options == null ? void 0 : options.sortable,
      sortBy,
      orderBy,
      onSortByChange
    };
  }, [
    columns,
    options == null ? void 0 : options.headerRowStyle,
    props.detailPanel,
    props.detailRows,
    props.actions,
    props.components,
    options == null ? void 0 : options.showDetailPanelOnRowClick,
    options == null ? void 0 : options.showDetailPanelArrow,
    defaultHeaderStyle,
    options == null ? void 0 : options.headerCellStyle,
    options == null ? void 0 : options.subHeaderCellStyle,
    options == null ? void 0 : options.subHeaderRowStyle,
    options == null ? void 0 : options.rowSelectable,
    options == null ? void 0 : options.sortable,
    sortBy,
    orderBy,
    onSortByChange
  ]);
  React14.useEffect(() => {
    if (props.onTableHeaderPropsChange) {
      props.onTableHeaderPropsChange(tableHeaderProps);
    }
  }, [tableHeaderProps]);
  React14.useImperativeHandle(ref, () => {
    return {
      rootRef: rootRef.current
    };
  }, [rootRef.current]);
  React14.useEffect(() => {
    let resizeObserver = new ResizeObserver((entries) => {
      let height = get(entries, "[0].contentRect.height", void 0);
      setRootHeight(height ? `${height}px` : "100%");
    });
    if (rootRef.current) {
      resizeObserver.observe(rootRef.current);
      setCustomMaterialTableCompScrollEl(rootRef.current);
    }
    return () => {
      setRootHeight("100%");
      resizeObserver.disconnect();
    };
  }, [rootRef.current]);
  return /* @__PURE__ */ React14.createElement(React14.Fragment, null, /* @__PURE__ */ React14.createElement(
    Box,
    {
      ref: rootRef,
      sx: __spreadValues(__spreadValues({
        borderTopLeftRadius: "4px",
        borderTopRightRadius: "4px",
        borderBottomLeftRadius: get(options, "pagination", false) ? "0" : "4px",
        borderBottomRightRadius: get(options, "pagination", false) ? "0" : "4px",
        border: "1px solid #F1F1F1",
        position: "relative"
      }, data.length === 0 && !props.isLoading ? {
        minHeight: "200px"
      } : {}), get(options, "rootSx", {}))
    },
    /* @__PURE__ */ React14.createElement(
      Table,
      {
        sx: [
          {
            position: data.length === 0 && !props.isLoading ? "absolute" : "relative",
            borderRight: "1px solid #F1F1F1"
          },
          get(options, "tableSx", {})
        ]
      },
      /* @__PURE__ */ React14.createElement(
        CustomMaterialTableHeader,
        __spreadProps(__spreadValues({}, tableHeaderProps), {
          headerRowStyle: __spreadProps(__spreadValues({}, tableHeaderProps == null ? void 0 : tableHeaderProps.headerRowStyle), {
            visibility: totalStickyTop ? "hidden" : "visible"
          })
        })
      ),
      /* @__PURE__ */ React14.createElement(TableBody, null, data.map((item, i) => {
        return /* @__PURE__ */ React14.createElement(React14.Fragment, { key: i }, /* @__PURE__ */ React14.createElement(
          CustomMaterialTableRow,
          {
            loading: props.isLoading,
            data: item,
            columns,
            options,
            rowIndex: i,
            totalRows: data.length,
            onRowClick: props.onRowClick,
            detailPanel: props.detailPanel,
            detailRows: props.detailRows,
            allRowsCollapse: props.allRowsCollapse,
            expandedDetailPanels: props.expandedDetailPanels,
            selectedArr: selectedRowsData,
            selectedRowIdentifier: props.selectedRowIdentifier,
            onChangeSelectedData: toggleSelectedData,
            actions: props.actions,
            components: props.components
          }
        ));
      }))
    ),
    data.length === 0 && !props.isLoading && /* @__PURE__ */ React14.createElement(
      Stack,
      {
        direction: "row",
        alignItems: "center",
        justifyContent: "center",
        sx: {
          position: "sticky",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: "100%",
          height: rootHeight
          // height: '100%'
        }
      },
      props.emptyTemplate || /* @__PURE__ */ React14.createElement(Typography, { variant: "subtitle2" }, "No Data Available")
    )
  ));
});
var CustomMaterialTableComp_default = CustomMaterialTableComp;
function ExportTableComp(props) {
  const exportCSV = () => {
    if (props.onExportCSV) {
      props.onExportCSV();
    }
  };
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(Tooltip, { title: "Export as CSV", placement: "top", arrow: true }, /* @__PURE__ */ React14__default.createElement(Stack, null, /* @__PURE__ */ React14__default.createElement(TableButton_default, { onClick: exportCSV, disabled: props.loadingExport }, /* @__PURE__ */ React14__default.createElement(
    FileDownloadOutlinedIcon,
    {
      sx: { fontSize: "20px", fontWeight: 600, mr: "13px" }
    }
  ), "Download"))));
}
function LeftRightScrollBtn(props) {
  var _a;
  const {
    parentScrollableElement,
    customMaterialTableCompScrollEl,
    totalStickyTop
  } = useCustomMaterialTableContext();
  const [canScrollLeft, setCanScrollLeft] = React14__default.useState(false);
  const [canScrollRight, setCanScrollRight] = React14__default.useState(false);
  const leftBtnRef = React14__default.useRef(null);
  const rightBtnRef = React14__default.useRef(null);
  const rootLocator = React14__default.useRef(null);
  const scrollFunc = (to) => {
    var _a2;
    const rootRef = (_a2 = props.tableComp.current) == null ? void 0 : _a2.rootRef;
    if (!rootRef || !(rootRef instanceof HTMLElement)) return;
    rootRef.scrollTo({
      behavior: "smooth",
      left: to === "left" ? 0 : rootRef.scrollWidth - rootRef.offsetWidth + 1
    });
  };
  React14__default.useEffect(() => {
    var _a2;
    const rootRef = (_a2 = props.tableComp.current) == null ? void 0 : _a2.rootRef;
    if (!rootRef || !(rootRef instanceof HTMLElement)) return;
    const handleScroll = () => {
      const maxScroll = rootRef.scrollWidth - rootRef.offsetWidth;
      const currentScroll = rootRef.scrollLeft;
      setCanScrollLeft(currentScroll > 0);
      setCanScrollRight(currentScroll < maxScroll);
    };
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(rootRef);
    rootRef.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      resizeObserver.disconnect();
      rootRef.removeEventListener("scroll", handleScroll);
    };
  }, [(_a = props.tableComp.current) == null ? void 0 : _a.rootRef]);
  React14__default.useEffect(() => {
    if (!parentScrollableElement || !customMaterialTableCompScrollEl) return;
    const isWindowScroll = parentScrollableElement === document.documentElement || parentScrollableElement === document.body;
    const handleScroll = throttle(() => {
      const tableBoundingClient = customMaterialTableCompScrollEl.getBoundingClientRect();
      const scrollableParentBoundingClient = parentScrollableElement.getBoundingClientRect();
      const scrollableParentTop = isWindowScroll ? 0 : scrollableParentBoundingClient.top;
      const scrollableParentBottom = isWindowScroll ? window.innerHeight : scrollableParentBoundingClient.bottom;
      const top = Math.max(tableBoundingClient.top, scrollableParentTop);
      const bottom = Math.min(
        tableBoundingClient.bottom,
        scrollableParentBottom
      );
      const position = `calc(${Math.abs(bottom - top) / 2 + (scrollableParentTop > tableBoundingClient.top ? scrollableParentTop - tableBoundingClient.top : 0)}px + calc(${totalStickyTop != null ? totalStickyTop : "0px"} * 0.5))`;
      if (leftBtnRef.current) leftBtnRef.current.style.top = position;
      if (rightBtnRef.current) rightBtnRef.current.style.top = position;
    }, 1e3 / 15);
    handleScroll();
    if (isWindowScroll) {
      window.addEventListener("scroll", handleScroll);
    } else {
      parentScrollableElement.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (isWindowScroll) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        parentScrollableElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [parentScrollableElement, customMaterialTableCompScrollEl, totalStickyTop]);
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement("div", { id: "root-locator", ref: rootLocator, style: { display: "none" } }), /* @__PURE__ */ React14__default.createElement(
    ButtonBase,
    {
      ref: leftBtnRef,
      onClick: () => scrollFunc("left"),
      sx: {
        border: "1px solid #FFF",
        width: "35px",
        height: "35px",
        borderRadius: "50%",
        bgcolor: "#5C5470",
        position: "absolute",
        left: "1px",
        zIndex: 6,
        visibility: canScrollLeft ? "visible" : "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "opacity 0.2s linear",
        opacity: 0.25,
        "&:hover": {
          opacity: 1
        }
      }
    },
    /* @__PURE__ */ React14__default.createElement(
      ChevronLeftRoundedIcon,
      {
        sx: {
          color: "#FFF",
          fontSize: "25px"
        }
      }
    )
  ), /* @__PURE__ */ React14__default.createElement(
    ButtonBase,
    {
      ref: rightBtnRef,
      onClick: () => scrollFunc("right"),
      sx: {
        border: "1px solid #FFF",
        width: "35px",
        height: "35px",
        borderRadius: "50%",
        bgcolor: "#5C5470",
        position: "absolute",
        right: "1px",
        zIndex: 6,
        visibility: canScrollRight ? "visible" : "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "opacity 0.2s linear",
        opacity: 0.25,
        "&:hover": {
          opacity: 1
        }
      }
    },
    /* @__PURE__ */ React14__default.createElement(
      ChevronRightRoundedIcon,
      {
        sx: {
          color: "#FFF",
          fontSize: "25px"
        }
      }
    )
  ));
}
var PaginationTableComp = React14__default.forwardRef((props, ref) => {
  const [page, setPage] = React14__default.useState(0);
  const [pageSize, setPageSize] = React14__default.useState(props.pageSize || 10);
  const { startIndex, endIndex } = React14__default.useMemo(() => {
    return {
      startIndex: page * pageSize,
      endIndex: (page + 1) * pageSize
    };
  }, [page, pageSize]);
  const pageSizeList = React14__default.useMemo(() => {
    let result = props.pageSizeList || [10, 20, 50];
    if (props.pageSize) {
      if (result.includes(props.pageSize)) {
        return result;
      } else {
        result = [props.pageSize, ...result];
        result.sort((a, b) => a - b);
        return result;
      }
    }
    return result;
  }, [props.pageSizeList, props.pageSize]);
  const totalPages = React14__default.useMemo(() => {
    return Math.ceil(props.totalDataLength / pageSize) - 1;
  }, [props.totalDataLength, pageSize]);
  const handlePageSizeChange = React14__default.useCallback(
    (event, child) => {
      const { value } = event.target;
      setPageSize(Number(value));
      if (pageSize * page > props.totalDataLength) {
        setPage(Math.floor(props.totalDataLength / pageSize));
      }
    },
    [pageSize, page]
  );
  const setPageIndex = React14__default.useCallback(
    (n) => {
      setPage(n);
    },
    [page]
  );
  React14__default.useImperativeHandle(
    ref,
    () => {
      return {
        setPageIndex
      };
    },
    [page]
  );
  React14__default.useEffect(() => {
    var _a;
    (_a = props.onChange) == null ? void 0 : _a.call({}, { startIndex, endIndex });
    if (props.totalDataLength > pageSize && page === Math.ceil(props.totalDataLength / pageSize) - 1 && // endIndex >= props.totalDataLength &&
    props.totalDataLength > 0 && props.onLastPage) {
      props.onLastPage();
    }
    if (props.totalDataLength > 0 && props.totalDataLength <= pageSize && props.onLastPage) {
      props.onLastPage();
    }
    if (startIndex > props.totalDataLength && endIndex > props.totalDataLength && props.totalDataLength > 0) {
      setPage(Math.floor(props.totalDataLength / pageSize) - 1);
    }
    if (startIndex < 0 || endIndex < 0) {
      setPage(0);
    }
  }, [page, pageSize, props.totalDataLength, props.onLastPage ? true : false]);
  React14__default.useEffect(() => {
    if (typeof props.pageNumber === "number") {
      setPage(props.pageNumber);
    }
  }, [props.pageNumber]);
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(
    Stack,
    {
      direction: "row",
      alignItems: "center",
      spacing: 1,
      sx: __spreadValues({ p: 2, display: "inline-flex" }, props.rootSx)
    },
    !props.hidePageSizeSelect && /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(Typography, { variant: "subtitle2", color: "#000" }, "Rows per page:"), /* @__PURE__ */ React14__default.createElement(FormControl, null, /* @__PURE__ */ React14__default.createElement(
      Select,
      {
        value: pageSize,
        onChange: handlePageSizeChange,
        size: "small",
        sx: {
          fontSize: "12px",
          boxShadow: "none",
          ".MuiOutlinedInput-notchedOutline": { border: 0 }
        }
      },
      pageSizeList.map((item, i) => {
        return /* @__PURE__ */ React14__default.createElement(MenuItem, { key: `${item}-${i}`, value: item }, item, " row", item > 1 ? "s" : "");
      })
    ))),
    /* @__PURE__ */ React14__default.createElement(
      IconButton,
      {
        size: "small",
        "data-testid": "pagination-prev",
        disabled: startIndex === 0,
        onClick: () => {
          if (props.onPaginationPrev) props.onPaginationPrev({ totalPages });
          else setPage((prev) => prev - 1);
        }
      },
      /* @__PURE__ */ React14__default.createElement(ChevronLeftIcon, null)
    ),
    /* @__PURE__ */ React14__default.createElement(Typography, { variant: "subtitle2", color: "#000" }, startIndex + 1, "-", Math.min(endIndex, props.totalDataLength), " of", " ", typeof props.totalPaginatedDataLength === "number" ? props.totalPaginatedDataLength : props.totalDataLength),
    /* @__PURE__ */ React14__default.createElement(
      IconButton,
      {
        size: "small",
        "data-testid": "pagination-next",
        disabled: endIndex >= props.totalDataLength,
        onClick: () => {
          if (props.onPaginationNext) props.onPaginationNext({ totalPages });
          else setPage((prev) => prev + 1);
        }
      },
      props.loadingNextPage ? /* @__PURE__ */ React14__default.createElement(CircularProgress, { size: 16 }) : /* @__PURE__ */ React14__default.createElement(ChevronRightIcon, null)
    )
  ));
});
var PaginationTableComp_default = PaginationTableComp;
var inputStyle = {
  width: "100%",
  border: "0px",
  outline: "none",
  background: "transparent",
  fontFamily: "Sora",
  "::placeholder": {
    color: "#0000004D"
  }
};
function SearchTableComp(props) {
  const [collapseIn, setCollapseIn] = React14__default.useState(!!props.initiallyExpanded);
  const [search, setSearch] = React14__default.useState(props.initialValue || "");
  const inputRef = React14__default.useRef(null);
  React14__default.useEffect(() => {
    let timer;
    if (props.onChange) {
      timer = setTimeout(() => {
        props.onChange(search);
      }, props.debounceMilliseconds || 0);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [search, props.debounceMilliseconds]);
  React14__default.useEffect(() => {
    var _a, _b;
    if (collapseIn) {
      (_a = inputRef.current) == null ? void 0 : _a.focus();
    } else {
      (_b = inputRef.current) == null ? void 0 : _b.blur();
    }
  }, [collapseIn]);
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(
    Stack,
    {
      direction: "row",
      alignItems: "center",
      className: "table-search",
      sx: {
        position: "relative",
        display: "inline-flex",
        bgcolor: "#F6F6F6",
        width: "100%",
        borderRadius: "4px",
        height: "40px",
        px: "16px",
        fontFamily: "Sora",
        "& input::-webkit-input-placeholder": {
          color: "rgba(0, 0, 0, 0.3)",
          fontSize: "14px"
        },
        "& input::-ms-input-placeholder": {
          color: "rgba(0, 0, 0, 0.3)",
          fontSize: "14px"
        },
        "& input::-moz-placeholder": {
          color: "rgba(0, 0, 0, 0.3)",
          fontSize: "14px"
        }
      }
    },
    /* @__PURE__ */ React14__default.createElement(
      SearchIcon,
      {
        sx: { fontSize: "20px", fontWeight: 600, mr: 1, color: "#C5C5C5" }
      }
    ),
    /* @__PURE__ */ React14__default.createElement(
      "input",
      {
        ref: inputRef,
        "data-testid": "search-input",
        placeholder: props.placeholder || "Search the table",
        value: search,
        onChange: (e) => {
          setSearch(e.target.value);
        },
        style: inputStyle
      }
    )
  ));
}
function SortByComp(props) {
  const [value, setValue] = React14__default.useState(
    props.initialOption ? typeof props.initialOption === "string" ? props.initialOption : props.initialOption.value : ""
  );
  const [orderBy, setOrderBy] = React14__default.useState(
    props.initialOrderBy || "ascending"
  );
  const onOptionChange = (value2) => {
    var _a;
    const option = props.options.find((option2) => option2.value === value2);
    (_a = props.onChange) == null ? void 0 : _a.call({}, option);
    setValue(value2);
  };
  const onOrderByChange = (orderType) => {
    var _a;
    (_a = props.onOrderByChange) == null ? void 0 : _a.call({}, orderType);
  };
  return /* @__PURE__ */ React14__default.createElement(Stack, { direction: "row", alignItems: "center", gap: "5px" }, /* @__PURE__ */ React14__default.createElement(
    SingleSelect_default,
    {
      options: props.options,
      value,
      onChange: onOptionChange,
      renderOption: props.renderOption,
      selectButtonSx: __spreadValues({
        height: "40px",
        width: "120px",
        bgcolor: "#F6f6f6",
        "&:hover": {
          bgcolor: "#EEEEEE"
        },
        borderRadius: "4px",
        px: "16px",
        py: "5px"
      }, props.selectSx),
      menuItemSx: props.menuItemSx,
      renderValue: (value2) => {
        var _a, _b;
        return /* @__PURE__ */ React14__default.createElement(
          Typography,
          {
            fontSize: "12px !important",
            fontWeight: 600,
            sx: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }
          },
          ((_b = (_a = props == null ? void 0 : props.options) == null ? void 0 : _a.find((e) => e.value === value2)) == null ? void 0 : _b.label) || "Sort by"
        );
      }
    }
  ), props.showOrderBy && /* @__PURE__ */ React14__default.createElement(
    IconButton,
    {
      size: "small",
      sx: {
        width: "40px",
        height: "40px",
        fontSize: "14px",
        fontWeight: 600,
        bgcolor: "#F6f6f6",
        "&:hover": {
          bgcolor: "#EEEEEE"
        },
        borderRadius: "4px",
        transform: `rotateZ(${orderBy === "ascending" ? 180 : 0}deg)`
      },
      "data-testid": "order-by-button",
      onClick: () => {
        setOrderBy((prev) => {
          let orderBy2 = prev === "ascending" ? "descending" : "ascending";
          onOrderByChange(orderBy2);
          return orderBy2;
        });
      }
    },
    /* @__PURE__ */ React14__default.createElement(FilterListIcon, { fontSize: "inherit" })
  ));
}
var RefreshTableComp = ({ onRefresh }) => {
  return /* @__PURE__ */ React14__default.createElement(React14__default.Fragment, null, /* @__PURE__ */ React14__default.createElement(TableButton_default, { onClick: onRefresh }, "Refresh", /* @__PURE__ */ React14__default.createElement(RefreshIcon, { sx: { fontSize: "20px", fontWeight: 600, ml: "13px" } })));
};
var RefreshTableComp_default = RefreshTableComp;

// src/components/CustomMaterialTable/views/CustomMaterialTable.tsx
function CustomMaterialTableTemplate(props) {
  var _a;
  const { options } = props;
  const { totalStickyTop, setCustomMaterialTableRootComp } = useCustomMaterialTableContext();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = React14.useState("data" in props ? props.data : []);
  const [totalPaginatedDataLength, setTotalPaginatedDataLength] = React14.useState();
  const [isLoading, setIsLoading] = React14.useState(
    "isLoading" in props ? props.isLoading : false
  );
  const paginationTableCompRef = React14.useRef();
  const customMaterialTableCompRef = React14.useRef();
  const [cloneHeaderProps, setCloneHeaderProps] = useState(null);
  const cloneHeaderRootRef = React14.useRef(null);
  const [cloneHeaderHeight, setCloneHeaderHeight] = useState(
    null
  );
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(get(options, "pageSize", 10));
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState(
    (options == null ? void 0 : options.initialSortableOption) ? generateSortableOptions(props.columns, options == null ? void 0 : options.initialSortableOption) : null
  );
  const [orderBy, setOrderBy] = useState(
    get(options, "initialOrderBy", "ascending")
  );
  const [resetPage, setResetPage] = React14.useState(false);
  const showToolbar = useMemo(() => {
    return !!(options == null ? void 0 : options.search) || !!((options == null ? void 0 : options.sortable) && (options == null ? void 0 : options.showSortableSelect)) || !!(options == null ? void 0 : options.export) || !!(options == null ? void 0 : options.refresh) || !!(options == null ? void 0 : options.expandable) || !!(options == null ? void 0 : options.extraToolbarComponent);
  }, [props]);
  const visibleData = useMemo(() => {
    if (!(options == null ? void 0 : options.search)) {
      return data;
    }
    let visibleColumns = props.columns.filter((col) => !col.hidden);
    let result = [];
    const customSearchFunctions = visibleColumns.reduce(
      (acc, col) => {
        if (col.onSearch) {
          set(acc, col.field, col.onSearch);
        }
        return acc;
      },
      {}
    );
    for (let i = 0; i < data.length; i++) {
      const copyData = (() => {
        try {
          return cloneDeep(data[i]);
        } catch (err) {
          return __spreadValues({}, data[i]);
        }
      })();
      let obj = {};
      const fieldsList = [];
      visibleColumns.forEach((col) => {
        fieldsList.push(col.field);
        set(obj, col.field, get(copyData, col.field, ""));
      });
      get(options, "extraSearchFields", []).forEach((field) => {
        fieldsList.push(field);
        set(obj, field, get(copyData, field, ""));
      });
      let found = fieldsList.some((key) => {
        const searchFunction = get(customSearchFunctions, key, null);
        if (searchFunction && typeof searchFunction === "function") {
          return searchFunction(copyData, searchText);
        }
        const item = get(obj, key, "");
        return item == null ? void 0 : item.toString().toLowerCase().includes(searchText.toLowerCase());
      });
      if (found) {
        result.push(__spreadValues({}, data[i]));
      }
    }
    return result;
  }, [data, props.columns, searchText]);
  const sortedData = useMemo(() => {
    var _a2;
    if (!sortBy) {
      return visibleData;
    }
    const ascending = orderBy === "ascending";
    const key = sortBy.value;
    const customOnSort = (_a2 = props.columns.find(
      (col) => (col == null ? void 0 : col.field) === key && !!(col == null ? void 0 : col.onSort)
    )) == null ? void 0 : _a2.onSort;
    function sortFunc(a, b) {
      if (!a || !b) return 1;
      if (customOnSort) {
        return customOnSort(a, b, { sortBy, orderBy });
      }
      const first = get(ascending ? a : b, key);
      const second = get(ascending ? b : a, key);
      if (typeof first === "number" || typeof second === "number") {
        return (first || 0) - (second || 0);
      } else if (typeof first === "string" || typeof second === "string") {
        const firstStr = String(first || "");
        const secondStr = String(second || "");
        return firstStr.localeCompare(secondStr);
      }
      return 0;
    }
    const result = JSON.parse(JSON.stringify(visibleData));
    result.sort(sortFunc);
    return result;
  }, [visibleData, sortBy, orderBy]);
  const tableData = useMemo(() => {
    if (options == null ? void 0 : options.pagination) {
      return sortedData.slice(startIndex, endIndex);
    }
    return sortedData;
  }, [startIndex, endIndex, options == null ? void 0 : options.pagination, sortedData]);
  const sortableOptions = useMemo(() => {
    const result = get(
      options,
      "extraSortableFields",
      []
    );
    result.push(...generateSortableOptions(props.columns));
    return result;
  }, [
    options == null ? void 0 : options.sortable,
    options == null ? void 0 : options.showSortableSelect,
    props.columns,
    options == null ? void 0 : options.extraSortableFields
  ]);
  const onOrderByChange = useCallback(
    (orderByType) => {
      if (props.onOrderByChange) {
        props.onOrderByChange(orderByType);
        return;
      }
      setOrderBy(orderByType);
    },
    [props.onOrderByChange, orderBy]
  );
  const onSortByChange = useCallback(
    (selectedOption) => {
      if (props.onSortByChange) {
        if (sortBy !== null) {
          setSortBy(null);
        }
        props.onSortByChange(selectedOption);
        return;
      }
      setSortBy(selectedOption);
    },
    [props.onSortByChange, sortBy]
  );
  const onSearchChange = useCallback(
    (searchText2) => {
      if (props.onSearchChange) {
        props.onSearchChange(searchText2);
        return;
      } else {
        setSearchText(searchText2);
      }
    },
    [props.onSearchChange, searchText]
  );
  const handleLastPage = React14.useCallback(() => {
    setResetPage(false);
    if (props.onLastPage) {
      props.onLastPage();
    }
  }, [props.onLastPage, resetPage]);
  const handleSortByChange = React14.useCallback(
    (selectedOption, orderBy2) => {
      onSortByChange(selectedOption);
      onOrderByChange(orderBy2);
    },
    [onSortByChange, onOrderByChange]
  );
  const handleTableHeaderPropsChange = React14.useCallback(
    (props2) => {
      setCloneHeaderProps(props2);
    },
    [cloneHeaderProps]
  );
  const onExportCSV = useCallback(() => {
    if (props.onExportCSV) {
      props.onExportCSV(props.columns, data);
      return;
    }
    let visibleColumns = props.columns.filter((col) => !col.hidden);
    let csvData = visibleData.map((item) => {
      return visibleColumns.map((col) => {
        return item[col.field];
      });
    });
    const fileName = get(options, "csvFileName", "data.csv");
    if (props.onExportCSVCompleted) {
      props.onExportCSVCompleted(fileName, "CSV");
    }
    let builder = new CsvBuilder(fileName);
    builder.setColumns(visibleColumns.map((col) => col.title)).addRows(csvData).exportFile();
  }, [props.onExportCSV, props.columns, data, visibleData]);
  React14.useEffect(() => {
    if (resetPage && typeof (options == null ? void 0 : options.pageNumber) !== "number") {
      if (paginationTableCompRef.current) {
        paginationTableCompRef.current.setPageIndex(0);
      }
    } else {
      setResetPage(true);
    }
  }, [data]);
  React14.useEffect(() => {
    if ("data" in props && Array.isArray(props.data)) {
      setData(props.data);
    }
  }, ["data" in props ? props.data : null]);
  React14.useEffect(() => {
    if ("isLoading" in props && typeof props.isLoading === "boolean") {
      setIsLoading(props.isLoading);
    }
  }, ["isLoading" in props ? props.isLoading : null]);
  React14.useEffect(() => {
    const tempTotalPaginatedDataLength = get(
      options,
      "totalPaginatedDataLength",
      void 0
    );
    if (typeof tempTotalPaginatedDataLength === "number") {
      setTotalPaginatedDataLength(tempTotalPaginatedDataLength);
    }
  }, [get(options, "totalPaginatedDataLength", void 0)]);
  React14.useEffect(() => {
    var _a2;
    const cleanUpFunctions = [];
    const cloneStickyHeader = cloneHeaderRootRef.current;
    const customMaterialTableCompRoot = (_a2 = customMaterialTableCompRef.current) == null ? void 0 : _a2.rootRef;
    if (cloneStickyHeader) {
      const resizeObserver = new ResizeObserver((entries) => {
        const height = get(entries, "[0].contentRect.height", null);
        setCloneHeaderHeight(height);
      });
      resizeObserver.observe(cloneStickyHeader);
      cleanUpFunctions.push(() => {
        resizeObserver.disconnect();
      });
    }
    if (cloneStickyHeader && customMaterialTableCompRoot) {
      const makeCellsWidthEqual = () => {
        const tableHeaderElements = customMaterialTableCompRoot.querySelectorAll("thead");
        const headTds = customMaterialTableCompRoot.querySelectorAll("thead td, thead th");
        const cloneHeadTds = cloneStickyHeader.querySelectorAll("thead td, thead th");
        const setWidthOfClone = () => {
          headTds.forEach((td, index) => {
            const cloneTd = cloneHeadTds[index];
            if (!cloneTd) return;
            cloneTd.style.minWidth = `${td.getBoundingClientRect().width}px`;
            cloneTd.style.maxWidth = cloneTd.style.minWidth;
          });
        };
        const resizeObserver = new ResizeObserver(() => {
          setWidthOfClone();
        });
        const mutationObserver = new MutationObserver(() => {
          setWidthOfClone();
        });
        tableHeaderElements.forEach((ele) => {
          mutationObserver.observe(ele, { attributes: true, childList: true });
        });
        headTds.forEach((td) => {
          resizeObserver.observe(td);
          mutationObserver.observe(td, {
            attributes: true,
            childList: true
          });
        });
        cleanUpFunctions.push(() => {
          resizeObserver.disconnect();
          mutationObserver.disconnect();
        });
      };
      makeCellsWidthEqual();
      const cloneScroll = () => {
        if (customMaterialTableCompRoot.scrollLeft !== cloneStickyHeader.scrollLeft) {
          customMaterialTableCompRoot.scrollLeft = cloneStickyHeader.scrollLeft;
        }
      };
      const rootScroll = () => {
        if (cloneStickyHeader.scrollLeft !== customMaterialTableCompRoot.scrollLeft) {
          cloneStickyHeader.scrollLeft = customMaterialTableCompRoot.scrollLeft;
        }
      };
      cloneStickyHeader.addEventListener("scroll", cloneScroll);
      customMaterialTableCompRoot.addEventListener("scroll", rootScroll);
      cleanUpFunctions.push(() => {
        cloneStickyHeader.removeEventListener("scroll", cloneScroll);
        customMaterialTableCompRoot.removeEventListener("scroll", rootScroll);
      });
    }
    return () => {
      cleanUpFunctions.forEach((func) => func());
    };
  }, [
    totalStickyTop,
    cloneHeaderRootRef.current,
    (_a = customMaterialTableCompRef.current) == null ? void 0 : _a.rootRef,
    cloneHeaderProps
    // clone header (using for sticky header) visibility depends on this state
  ]);
  const [allRowsCollapse, setAllRowsCollapse] = React14.useState(false);
  const toggleAllRows = () => {
    setAllRowsCollapse((prev) => !prev);
  };
  return /* @__PURE__ */ React14.createElement(ThemeProvider, null, /* @__PURE__ */ React14.createElement(
    Stack,
    {
      ref: (ele) => setCustomMaterialTableRootComp(ele),
      direction: "column",
      alignItems: "stretch",
      sx: __spreadProps(__spreadValues({
        width: "100%",
        minHeight: data.length === 0 ? "500px" : "auto"
      }, get(options, "rootSx", {})), {
        "& *": {
          boxSizing: "border-box"
        }
      })
    },
    props.titleComp && /* @__PURE__ */ React14.createElement(Box, { flex: 1 }, props.titleComp),
    /* @__PURE__ */ React14.createElement(
      Box,
      {
        sx: __spreadValues({}, totalStickyTop ? {
          position: "sticky",
          top: totalStickyTop,
          bgcolor: "#FFF",
          zIndex: 10
        } : {})
      },
      showToolbar && /* @__PURE__ */ React14.createElement(
        Stack,
        {
          direction: "row",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          spacing: 1,
          sx: __spreadValues({
            py: 1
          }, get(options, "toolbarSx", {}))
        },
        (options == null ? void 0 : options.search) && /* @__PURE__ */ React14.createElement(
          SearchTableComp,
          {
            placeholder: get(options, "searchPlaceholder", void 0),
            onChange: onSearchChange,
            initiallyExpanded: get(
              options,
              "initiallyExpandedSearch",
              false
            ),
            debounceMilliseconds: get(
              options,
              "debounceMilliseconds",
              void 0
            )
          }
        ),
        options == null ? void 0 : options.extraToolbarComponent,
        (options == null ? void 0 : options.sortable) && (options == null ? void 0 : options.showSortableSelect) && /* @__PURE__ */ React14.createElement(
          SortByComp,
          {
            options: sortableOptions,
            showOrderBy: get(options, "showOrderBy", void 0),
            initialOrderBy: get(options, "initialOrderBy", void 0),
            onChange: onSortByChange,
            onOrderByChange
          }
        ),
        (options == null ? void 0 : options.expandable) && /* @__PURE__ */ React14.createElement(
          CollapsableComp_default,
          {
            allRowsCollapse,
            onChange: toggleAllRows
          }
        ),
        (options == null ? void 0 : options.refresh) && props.onRefresh && /* @__PURE__ */ React14.createElement(RefreshTableComp_default, { onRefresh: props.onRefresh }),
        (options == null ? void 0 : options.export) && /* @__PURE__ */ React14.createElement(
          ExportTableComp,
          {
            onExportCSV,
            loadingExport: options.loadingExport
          }
        )
      ),
      totalStickyTop && !!cloneHeaderProps && /* @__PURE__ */ React14.createElement(
        Box,
        {
          ref: cloneHeaderRootRef,
          sx: {
            width: "100%",
            overflowX: "auto",
            "&::-webkit-scrollbar": { width: "0px", height: "0px" },
            bgcolor: "#FFF"
          }
        },
        /* @__PURE__ */ React14.createElement(Table, null, /* @__PURE__ */ React14.createElement(
          CustomMaterialTableHeader,
          __spreadProps(__spreadValues({}, cloneHeaderProps), {
            headerRowStyle: __spreadProps(__spreadValues({}, get(cloneHeaderProps, "headerRowStyle", {})), {
              display: "table-row"
            })
          })
        ))
      )
    ),
    /* @__PURE__ */ React14.createElement(
      Box,
      {
        id: "view-table-id",
        style: __spreadValues({}, totalStickyTop ? {
          marginTop: `-${typeof cloneHeaderHeight === "number" ? cloneHeaderHeight : 52}px`
        } : {}),
        sx: __spreadValues({
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative"
        }, get(options, "tableBoxSx", {}))
      },
      /* @__PURE__ */ React14.createElement(
        CustomMaterialTableComp_default,
        {
          ref: customMaterialTableCompRef,
          data: tableData,
          columns: props.columns,
          isLoading,
          options: __spreadProps(__spreadValues({
            rootSx: (() => {
              let rootSx = __spreadValues({
                flex: 1,
                height: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                position: "relative",
                "&::-webkit-scrollbar": { width: "5px", height: "5px" },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "10px",
                  background: "#B2B2B2"
                }
              }, options == null ? void 0 : options.tableInnerBoxSx);
              if (props.options && props.options.rootSx) {
                delete props.options.rootSx;
              }
              return rootSx;
            })()
          }, props.options), {
            // headerRowStyle: {
            //   ...props?.options?.headerRowStyle
            // },
            sortable: (options == null ? void 0 : options.sortable) && !(options == null ? void 0 : options.showSortableSelect)
          }),
          selectedRowIdentifier: props.selectedRowIdentifier,
          onRowClick: props.onRowClick,
          onSelectionChange: props.onSelectionChange,
          emptyTemplate: props.emptyTemplate,
          detailPanel: props.detailPanel,
          detailRows: props.detailRows,
          allRowsCollapse,
          expandedDetailPanels: props.expandedDetailPanels,
          actions: props.actions,
          components: props.components,
          onSortByChange: handleSortByChange,
          onTableHeaderPropsChange: handleTableHeaderPropsChange
        }
      ),
      /* @__PURE__ */ React14.createElement(LeftRightScrollBtn, { tableComp: customMaterialTableCompRef })
    ),
    (options == null ? void 0 : options.pagination) && /* @__PURE__ */ React14.createElement(
      Stack,
      {
        direction: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        sx: {
          p: sm ? 0 : 0.5,
          border: "1px solid #F1F1F1",
          borderTop: "none",
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px"
        }
      },
      /* @__PURE__ */ React14.createElement(
        PaginationTableComp_default,
        {
          ref: paginationTableCompRef,
          hidePageSizeSelect: sm,
          pageSize: options == null ? void 0 : options.pageSize,
          pageNumber: options == null ? void 0 : options.pageNumber,
          onPaginationPrev: props.onPaginationPrev,
          onPaginationNext: props.onPaginationNext,
          totalDataLength: sortedData.length,
          totalPaginatedDataLength,
          pageSizeList: options == null ? void 0 : options.pageSizeList,
          onLastPage: handleLastPage,
          loadingNextPage: options.loadingNextPage,
          rootSx: { p: sm ? 0 : void 0 },
          onChange: ({ startIndex: startIndex2, endIndex: endIndex2 }) => {
            setStartIndex(startIndex2);
            setEndIndex(endIndex2);
          }
        }
      )
    )
  ));
}
function CustomMaterialTable(props) {
  const { options = {} } = props;
  return /* @__PURE__ */ React14.createElement(ErrorBoundary_default, { componentName: "CustomMaterialTable" }, /* @__PURE__ */ React14.createElement(CustomMaterialTableContextProvider, { stickyHeader: options == null ? void 0 : options.stickyHeader }, /* @__PURE__ */ React14.createElement(CustomMaterialTableTemplate, __spreadValues({}, props))));
}

export { CollapsableComp_default, CustomMaterialTable, CustomMaterialTableComp_default, CustomMaterialTableRow, ExportTableComp, LeftRightScrollBtn, PaginationTableComp_default, SearchTableComp, SortByComp, TableButton_default };
//# sourceMappingURL=chunk-GMBD7EOR.js.map
//# sourceMappingURL=chunk-GMBD7EOR.js.map